import { Select, SelectOption } from '@ardoq/select';
import {
  APIServiceAccount,
  APICurrentUser,
  RunAs,
  ArdoqId,
  APIOrganizationUser,
} from '@ardoq/api-types';
import { IconName } from '@ardoq/icons';
import { type Locale, localeCompare } from '@ardoq/locale';
import { type I18nLocale, t } from '@ardoq/i18n';

type RunAsSelectorProps = {
  value: RunAs;
  onValueChange: (value: RunAs) => void;
  label?: string;
  serviceAccounts: APIServiceAccount[];
  currentUser: APICurrentUser;
  isLoading?: boolean;
  hasError?: boolean;
  orgUsersById: Record<ArdoqId, APIOrganizationUser>;
  i18nLocale: I18nLocale;
  locale: Locale;
};

export const RunAsSelector = ({
  value,
  onValueChange,
  label,
  serviceAccounts,
  currentUser,
  isLoading = false,
  hasError,
  orgUsersById,
  i18nLocale,
  locale,
}: RunAsSelectorProps) => {
  const resolvedLabel =
    label ??
    t(i18nLocale, 'integrations:runAsSelector.executeAsLabel', 'Execute as');
  const currentUserOption: SelectOption<RunAs> = {
    label: currentUser.name,
    value: { type: 'user', id: currentUser._id },
    leftIcon: { name: IconName.PERSON },
  };

  const maybeSelectedUserOption: SelectOption<RunAs> | undefined =
    value &&
    value.type === 'user' &&
    value.id !== '' &&
    value.id !== currentUser._id
      ? {
          label:
            orgUsersById[value.id]?.name ??
            t(
              i18nLocale,
              'integrations:runAsSelector.unknownUser',
              'Unknown User'
            ),
          value: { type: 'user', id: value.id },
          leftIcon: { name: IconName.PERSON },
        }
      : undefined;

  const userOptions: SelectOption<RunAs>[] = [
    currentUserOption,
    ...(maybeSelectedUserOption ? [maybeSelectedUserOption] : []),
  ].sort((a, b) => localeCompare(a.label, b.label, locale));

  const serviceAccountOptions: SelectOption<RunAs>[] = serviceAccounts
    .map((account): SelectOption<RunAs> => ({
      label: account.user.name,
      value: { type: 'service-account', id: account.user._id },
      leftIcon: { name: IconName.ACCESS_SERVICE_ACCOUNT },
    }))
    .sort((a, b) => localeCompare(a.label, b.label, locale));

  const options = [
    {
      label: t(i18nLocale, 'integrations:runAsSelector.usersGroup', 'Users'),
      options: userOptions,
    },
    {
      label: t(
        i18nLocale,
        'integrations:runAsSelector.serviceAccountsGroup',
        'Service Accounts'
      ),
      options: serviceAccountOptions,
    },
  ];

  const selectValue = [...userOptions, ...serviceAccountOptions].find(
    option => option.value.id === value?.id
  );

  const handleValueChange = (newValue: RunAs | null) => {
    if (newValue) {
      onValueChange(newValue);
    }
  };

  return (
    <Select
      isDisabled={isLoading}
      label={resolvedLabel}
      value={selectValue}
      options={options}
      hasError={hasError}
      onValueChange={handleValueChange}
      i18nLocale={i18nLocale}
    />
  );
};
